import { ApiError } from "@api/errors";
import {
  measurementsCreateRows,
  measurementsDeleteRows,
  measurementsLineKtExecute,
  measurementsLineKtPreview,
  measurementsRows,
  measurementsSubplotAverage,
  measurementsUpdateCells,
  useMeasurementsOverview,
  useMeasurementsTables,
} from "@api/generated/measurements/measurements";
import type { CellChangeIn, KtPlan, KtPlotIn, TrialOverviewOut } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { hasRole, useGridViews, useSaveSetting, useSeasonFromLink, useSettings, useTableSettings } from "@app/workspace";
import { HabitModal } from "@features/genealogy/HabitModal";
import { linesOf } from "@features/genealogy/habit";
import { CellPanel, cellTitle } from "@features/trials/CellPanel";
import { ColumnsDialog } from "@features/trials/ColumnsDialog";
import {
  type CellConflictInfo,
  EditableGrid,
  type FocusedCell,
  type GridHandle,
  type GridViewState,
  gridSettingsId,
  type Row,
  type Selection,
} from "@features/trials/EditableGrid";
import { editKey, useMeasurementEdits } from "@features/trials/edits";
import { FillDialog } from "@features/trials/FillDialog";
import { ktProblemText, LineKtModal } from "@features/trials/LineKtModal";
import { PlotRange } from "@features/trials/PlotRange";
import { TrialBrowser, TrialSideList } from "@features/trials/TrialBrowser";
import { dataCount, dataSites, groupOverview, isTrialGroup, resolveTrial, trialGroupTooBroad } from "@features/trials/trialGroup";
import { hint } from "@i18n";
import {
  Button,
  Checkbox,
  Group,
  Kbd,
  Loader,
  Menu,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useShell } from "@shell/context";
import { ConfirmModal, SHORTCUTS } from "@shell/Dialogs";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ActionMenu } from "@ui/ActionMenu";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { BackButton } from "@ui/BackButton";
import { EditsBar } from "@ui/EditsBar";
import { formatNumber, num, shortTime } from "@ui/format";
import { LoadingBar } from "@ui/LoadingBar";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { editCounts, refreshEdits } from "@ui/recentEdits";
import { SaveState } from "@ui/SaveState";
import { SelectionBar } from "@ui/SelectionBar";
import { Splitter } from "@ui/Splitter";
import { FileButtons } from "@ui/table/FileButtons";
import { FilterRowButton, ResetTableButton, ShownNote, SortNote, useFilterRow } from "@ui/table/TableState";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const search = z.object({
  ev: z.number().int().optional(),
  // one plot (a link from a plot's history or quick search), or a plot range of the table (legacy Manual Input
  // "Parc: From - To"); ``parc`` wins
  parc: z.number().int().optional(),
  parc_from: z.number().int().optional(),
  parc_to: z.number().int().optional(),
  alp: z.number().int().optional(),
  paz: z.number().int().optional(),
  sid: z.number().int().optional(),
  table: z.string().default("trms"),
  kis: z.string().optional(),
  th: z.string().optional(),
  // the start's picked trial (not opened yet) and its list of trials without the data (what is still missing)
  pick: z.string().optional(),
  missing: z.boolean().default(false),
  // the opened trial's list of the trials with the same data, beside the grid
  list: z.boolean().default(true),
  grid_view: z.string().optional(),
  q: z.string().default(""),
  columns: z.array(z.string()).max(400).optional(),
  details: z.boolean().default(false),
  all: z.boolean().default(false),
});

export const Route = createFileRoute("/trials/data")({
  validateSearch: search,
  component: TrialData,
});

const PAGE = 5000;
const KT_PLOTS = 500; // the kt-to-line API's plots per request (line_kt.MAX_PLOTS)
const GRID_SHORTCUTS = SHORTCUTS.find((g) => g.group === "inGrid")?.items ?? [];
type Dialog = "columns" | "rows" | "average" | "delete" | "habit" | "fill" | null;

function TrialData() {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const client = useQueryClient();
  const ev = useSeasonFromLink(params.ev);
  const kis = params.kis;
  const me = useUserMe();
  const canEdit = hasRole(me.data?.roles, "nemesito");
  const tables = useMeasurementsTables({ query: { staleTime: Number.POSITIVE_INFINITY } });
  const overview = useMeasurementsOverview(ev ?? 0, { query: { enabled: !!ev } });
  const trials = overview.data ?? [];
  // a trial group (MV24*: legacy Manual Input) is shown as one trial: every matching trial's rows in one grid
  const group = isTrialGroup(kis) ? groupOverview(kis, trials) : null;
  const trial = group ?? trials.find((o) => o.kis === kis);
  const [trialSearch, setTrialSearch] = useState("");
  const typedGroup = trialSearch.trim();
  const table = tables.data?.find((x) => x.name === params.table);
  // the first site with the shown data, else the trial's first
  const th = params.th ?? (trial && dataSites(trial, table)[0]?.[0]) ?? trial?.sites[0];
  const register = !!table?.register;
  const settings = useSettings();
  const saveSetting = useSaveSetting();
  const defaultTh = settings?.default_th ?? "MV";
  // Szf: a typed KT goes to the line's register rows and Pheno too (the legacy Manual Input option; off = Szf only)
  const ktToLine = params.table === "szf" && !!settings?.szf_kt_to_line;
  const views = useGridViews(params.table);
  const view = views.views.find((v) => v.name === params.grid_view);
  const grid = useRef<GridHandle>(null);
  // the plots a link points at: one plot or a line's plots (the table of the plot register, else the shown table)
  const scope = { parc: params.parc, alp: params.alp, paz: params.paz, sid: params.sid, scope_table: register ? undefined : params.table };
  const [cell, setCell] = useState<FocusedCell | null>(null);
  const panel = params.details;
  const setPanel = (details: boolean) => navigate({ search: (s) => ({ ...s, details }), replace: true });
  const [selection, setSelection] = useState<Selection | null>(null);
  const [conflicts, setConflicts] = useState<CellConflictInfo[]>([]);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const showEmpty = params.all;
  const filter = params.q;
  const setShowEmpty = (all: boolean) => navigate({ search: (s) => ({ ...s, all }), replace: true });
  const setFilter = (q: string) => navigate({ search: (s) => ({ ...s, q }), replace: true });

  const parcFrom = params.parc ?? params.parc_from;
  const parcTo = params.parc ?? params.parc_to;
  const rowsKey = ["measurements", ev, params.table, register ? null : th, kis, scope, parcFrom, parcTo] as const;
  const rows = useInfiniteQuery({
    queryKey: rowsKey,
    enabled: !!ev && !!table && !!kis,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      measurementsRows(
        ev ?? 0,
        params.table,
        {
          th: register ? undefined : th,
          kis,
          ...scope,
          parc_from: parcFrom,
          parc_to: parcTo,
          limit: PAGE,
          cursor: pageParam,
        },
        { signal },
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const allRows = useMemo(() => rows.data?.pages.flatMap((p) => p.rows) ?? [], [rows.data]) as Row[];
  const reload = () => {
    void client.invalidateQueries({ queryKey: rowsKey });
    void client.invalidateQueries({ queryKey: ["trial-history"] });
  };
  const [saving, setSaving] = useState(0);
  const markSaved = () => setSavedAt(shortTime(new Date().toISOString(), true));
  const [gridView, setGridView] = useState<(GridViewState & { grid: string }) | null>(null);
  const gridKey = `${table?.name}|${kis}|${th}`;
  const shownView = gridView?.grid === gridKey ? gridView : null;
  const filtered = shownView?.columns ?? 0;
  const [filterRow, setFilterRow] = useFilterRow(filtered);
  // the grid's kept layout (EditableGrid keeps it): the Reset table button is offered while there is one
  const [layout] = useTableSettings(gridSettingsId(params.table));
  // cells typed lately (everyone's, refreshed while the page is open): coloured, and put back from the bar above
  const shownTables = useMemo(() => (table ? [table] : []), [table]);
  const edits = useMeasurementEdits(shownTables);
  const rowIds = useMemo(() => new Set(allRows.map((r) => String(r[table?.key ?? ""]))), [allRows, table]);
  // the lines of the selected rows (the register's own PAz / SID, a measurement row's plot's), each once
  const selectedLines = useMemo(
    () => linesOf(allRows, selection?.rowIds ?? [], table?.key ?? "", register),
    [allRows, selection, table, register],
  );
  const editCount = useMemo(() => editCounts([...edits.values()].map((x) => (rowIds.has(x.rowId) ? x.edit : undefined))), [edits, rowIds]);
  const focusedEdit = table && cell ? edits.get(editKey(table.name, cell.row[table.key], cell.column.name))?.edit : undefined;
  const [undoable, setUndoable] = useState({ grid: "", count: 0 });
  const undoCount = undoable.grid === gridKey ? undoable.count : 0;

  // KT → the line: preview; fills (and no differing KT) are written at once, differing ones are asked about. Plans
  // waiting for an answer queue up (a KT typed while the question is open is asked next); a queued plan whose rows an
  // earlier answer changed is previewed again (plan_changed) and asked anew.
  const [ktQueue, setKtQueue] = useState<{ plots: KtPlotIn[]; plan: KtPlan }[]>([]);
  const kt = ktQueue[0];
  const [ktRunning, setKtRunning] = useState(false);
  const ktNext = () => setKtQueue((q) => q.slice(1));
  const ktRun = (plots: KtPlotIn[], overwrite: boolean, token: string) => {
    setKtRunning(true);
    return measurementsLineKtExecute(ev ?? 0, { plots, overwrite, token })
      .then((r) => {
        if (r.operation)
          notifications.show({ color: "green", message: t("trialData.headType.transferred", { alap: r.alap, pheno: r.pheno }) });
      })
      .finally(() => setKtRunning(false));
  };
  const ktPlan = (plots: KtPlotIn[]) =>
    measurementsLineKtPreview(ev ?? 0, { plots }).then((plan) => {
      const problems = ktProblemText(plan, t); // the question below says them itself
      if (problems && !plan.kept) notifications.show({ color: "orange", message: problems, autoClose: 8000 });
      if (plan.kept > 0) setKtQueue((q) => [...q, { plots, plan }]);
      else if (plan.fills + plan.overwrites > 0) return ktRun(plots, false, plan.token);
    });
  const carryKt = (changes: CellChangeIn[]) => {
    const plots = changes
      .filter((c) => c.column === "kt" && c.new != null && String(c.new).trim())
      .map((c) => ({ row_id: c.row_id, kt: String(c.new) }));
    if (!ktToLine || !ev || !plots.length) return;
    // one preview per KT_PLOTS plots (the API's limit), one after another: a large paste is carried in parts
    let chain = Promise.resolve();
    for (let i = 0; i < plots.length; i += KT_PLOTS) chain = chain.then(() => ktPlan(plots.slice(i, i + KT_PLOTS)));
    chain.catch(notifyError);
  };
  const ktAnswer = (overwrite: boolean) => {
    if (!kt) return;
    const run = overwrite
      ? measurementsLineKtPreview(ev ?? 0, { plots: kt.plots, overwrite: true }).then((plan) => ktRun(kt.plots, true, plan.token))
      : ktRun(kt.plots, false, kt.plan.token);
    run
      .then(ktNext)
      .catch((error: unknown) => {
        if (!(error instanceof ApiError && error.code === "plan_changed")) throw error;
        ktNext();
        return ktPlan(kt.plots);
      })
      .catch(notifyError);
  };
  const save = async (changes: CellChangeIn[]) => {
    setSaving((n) => n + 1);
    setSavedAt(null);
    try {
      await measurementsUpdateCells(ev ?? 0, params.table, { changes });
      refreshEdits(client);
    } finally {
      setSaving((n) => n - 1);
    }
  };
  const done = (message: string) => {
    notifications.show({ color: "green", message });
    setDialog(null);
    reload();
  };

  // the trials with the shown data (the legacy Existent Trials): the list beside the grid
  const existent = useMemo(() => trials.filter((o) => dataCount(o, table) > 0), [trials, table]);
  // another trial at the same site when it has the data there
  const goTrial = useCallback(
    (o: TrialOverviewOut | undefined) =>
      o && navigate({ search: (s) => ({ ...s, kis: o.kis, th: th && o.table_sites[s.table]?.[th] ? th : undefined }) }),
    [navigate, th],
  );
  const toList = useCallback(
    (missing: boolean) =>
      navigate({
        search: (s) => ({ ...s, kis: undefined, th: undefined, missing, pick: missing || isTrialGroup(s.kis) ? undefined : s.kis }),
      }),
    [navigate],
  );
  const setList = useCallback((list: boolean) => navigate({ search: (s) => ({ ...s, list }), replace: true }), [navigate]);
  const allTables = tables.data ?? [];
  const tabs = showEmpty || !trial ? allTables : allTables.filter((x) => x.register || dataCount(trial, x) > 0 || x.name === params.table);

  if (!kis) {
    return (
      <div className="page fill trial-data-page">
        <PageHeader title={t("trialData.title")} season subtitle={t("trialData.browse.description", { ev })} />
        {ev && (
          <TrialBrowser
            ev={ev}
            trials={trials}
            tables={allTables}
            loading={overview.isPending}
            error={overview.error}
            retry={overview.refetch}
            state={{ table: params.table, pick: params.pick, missing: params.missing }}
            onChange={(next) =>
              navigate({
                search: (s) => ({
                  ...s,
                  ...next,
                  ...(next.table !== s.table && { grid_view: undefined, columns: undefined }),
                }),
                replace: true,
              })
            }
            onOpen={(open, site) => navigate({ search: (s) => ({ ...s, kis: open, th: site, pick: undefined, missing: false }) })}
          />
        )}
      </div>
    );
  }
  const counts = tabs.map((x) => (trial ? dataCount(trial, x) : 0));
  const usual = mostCommon(counts);
  const selected = selection && selection.cells > 1 ? selection : null;
  const canDelete = canEdit && !register;
  const sites = trial?.sites ?? [];

  return (
    <div className="page fill trial-data-page">
      <PageHeader
        title={t("trialData.title")}
        season
        pinSearch={{ th, columns: params.columns ?? view?.columns }}
        // remembered once the table is known, not under a name without it
        item={ev && table ? { kind: "trial", title: `${kis} · ${table.label}`, subtitle: `${ev}${th ? ` · ${th}` : ""}` } : undefined}
        subtitle={
          <span className="hstack" style={{ gap: 8 }}>
            <Select
              size="sm"
              w={220}
              placeholder={t("trialData.chooseTrial")}
              aria-label={t("trialData.trial")}
              searchable
              searchValue={trialSearch}
              onSearchChange={setTrialSearch}
              data={[
                // a typed group (MV24*) is offered too, and the group being shown
                ...[...new Set([group?.kis, isTrialGroup(typedGroup) && !typedGroup.includes(" ") ? typedGroup : undefined])]
                  .filter(isTrialGroup)
                  .map((g) => ({ value: g, label: t("trialData.groupSummary", { sample: g, count: groupOverview(g, trials).members }) })),
                ...trials.map((o) => ({
                  value: o.kis,
                  label: `${o.kis} (${formatNumber(Math.max(o.register_plots, ...Object.values(o.tables)))})`,
                })),
              ]}
              value={kis}
              onChange={(v) => {
                if (isTrialGroup(v ?? undefined)) void navigate({ search: (s) => ({ ...s, kis: v ?? undefined, th: undefined }) });
                else if (v) goTrial(trials.find((o) => o.kis === v));
              }}
            />
            {sites.length > 0 &&
              (sites.length <= 4 ? (
                <SegmentedControl
                  size="sm"
                  disabled={register}
                  aria-label={t("trialData.site")}
                  data={sites}
                  value={th ?? ""}
                  onChange={(v) => navigate({ search: (s) => ({ ...s, th: v }) })}
                />
              ) : (
                <Select
                  size="sm"
                  w={110}
                  disabled={register}
                  aria-label={t("trialData.site")}
                  data={sites}
                  value={th ?? null}
                  onChange={(v) => v && navigate({ search: (s) => ({ ...s, th: v }) })}
                />
              ))}
          </span>
        }
        actions={
          <Group gap={8}>
            <ActionMenu
              size="xs"
              actions={[
                {
                  label: t("trialData.subplotAverage"),
                  onClick: () => setDialog("average"),
                  hidden: !canEdit || register || !th || !!group,
                },
                {
                  label: t("trialData.prepare"),
                  onClick: () => setDialog("rows"),
                  hidden: !canEdit || register || !trial?.register_plots,
                },
                {
                  label: t("register.habit.action"),
                  onClick: () => setDialog("habit"),
                  hidden: !canEdit || !selectedLines.length,
                },
                {
                  label: t("trialData.deleteSelected", { count: selection?.rowIds.length ?? 0 }),
                  icon: "trash",
                  danger: true,
                  onClick: () => setDialog("delete"),
                  hidden: !canDelete || !selection?.rowIds.length,
                },
              ]}
            />
          </Group>
        }
      />

      <Tabs
        value={params.table}
        onChange={(v) =>
          v &&
          navigate({
            search: (s) => ({ ...s, table: v, grid_view: undefined, columns: undefined }),
          })
        }
        color="breeder"
      >
        <AnimatedTabsList style={{ flexWrap: "nowrap", overflowX: "auto" }}>
          {tabs.map((x, i) => (
            <Tabs.Tab
              key={x.name}
              value={x.name}
              rightSection={trial && counts[i] !== usual ? <span className="mu sm">{formatNumber(counts[i])}</span> : undefined}
            >
              {x.label}
            </Tabs.Tab>
          ))}
          {tabs.length < allTables.length && (
            <button type="button" className="chip" style={{ alignSelf: "center", marginLeft: 8 }} onClick={() => setShowEmpty(true)}>
              {t("trialData.emptyTables", { count: allTables.length - tabs.length })}
            </button>
          )}
        </AnimatedTabsList>
      </Tabs>

      <div id="trial-table-panel" className="fill-row">
        {params.list && (
          <>
            <TrialSideList
              trials={existent}
              table={table}
              kis={kis}
              missing={trials.length - existent.length}
              onPick={goTrial}
              toList={toList}
              setList={setList}
            />
            <Splitter
              cssVar="--trial-side-w"
              side="start"
              min={150}
              max={480}
              label={t("trialData.browse.listWidth")}
              className="trial-side-splitter"
            />
          </>
        )}
        <div className="grow stack" style={{ gap: 8 }}>
          <div className="hstack" style={{ gap: 10, fontSize: "var(--b-text-small, 0.75rem)", flexWrap: "wrap" }}>
            {!params.list && (
              <>
                <BackButton size="compact-sm" onClick={() => toList(false)}>
                  {t("trialData.browse.backToList")}
                </BackButton>
                <Button size="compact-xs" variant="default" onClick={() => setList(true)}>
                  {t("trialData.browse.showList")}
                </Button>
              </>
            )}
            <TextInput
              size="xs"
              w={200}
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
              placeholder={t("trialData.filterPlaceholder")}
              aria-label={t("trialData.filterPlaceholder")}
              leftSection={<Icon name="search" size={14} />}
            />
            <PlotRange
              size="xs"
              from={params.parc_from ?? ""}
              to={params.parc_to ?? ""}
              onFrom={(v) => navigate({ search: (s) => ({ ...s, parc_from: num(v) ?? undefined }), replace: true })}
              onTo={(v) => navigate({ search: (s) => ({ ...s, parc_to: num(v) ?? undefined }), replace: true })}
              fromLabel={t("trialData.plotFrom")}
              toLabel={t("trialData.plotTo")}
              inline
            />
            {(params.parc != null || params.paz != null) && (
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => navigate({ search: (s) => ({ ...s, parc: undefined, alp: undefined, paz: undefined, sid: undefined }) })}
              >
                {t("trialData.showAllPlots")}
              </Button>
            )}
            {params.table === "szf" && canEdit && (
              <Tooltip label={t("trialData.headType.help")} multiline w={340}>
                <Checkbox
                  size="xs"
                  label={t("trialData.headType.applyToLine")}
                  checked={ktToLine}
                  onChange={(e) => saveSetting("szf_kt_to_line", e.currentTarget.checked)}
                />
              </Tooltip>
            )}
            <SaveState saving={saving > 0} saved={savedAt && t("trialData.savedAt", { time: savedAt })} />
            {!canEdit && <span className="mu">{t("trialData.readOnly")}</span>}
            {rows.isFetching && <Loader size="xs" />}
            <div style={{ width: 60 }}>
              <LoadingBar active={saving > 0} label={t("async.saving")} />
            </div>
            <div style={{ flexGrow: 1 }} />
            {(["cell-readonly", "row-standard", "cell-conflict", "cell-invalid"] as const).map((cls) => (
              <span key={cls} className="grid-legend">
                <i className={`legend-${cls}`} />
                {t(`trialData.legend.${cls}`)}
              </span>
            ))}
            <Button
              size="compact-sm"
              variant={panel ? "light" : "default"}
              leftSection={<Icon name="history" size={14} />}
              aria-pressed={panel}
              onClick={() => setPanel(!panel)}
            >
              {t("trialData.history.title")}
            </Button>
          </div>
          <p className="note phone-only">
            {t("trialData.fieldModeSuggestion")} <Link to="/field">{t("trialData.fieldModeLink")}</Link>
          </p>
          {table && allRows.length > 0 && (
            <EditsBar
              counts={editCount}
              focused={cell && focusedEdit ? { name: cellTitle(cell), edit: focusedEdit } : null}
              onPutBack={
                canEdit && cell && focusedEdit
                  ? () => grid.current?.setCell(Number(cell.row[table.key]), cell.column.name, focusedEdit.before ?? null)
                  : undefined
              }
              onEarlier={() => setPanel(true)}
              undoable={undoCount}
              onUndo={canEdit ? () => grid.current?.undo() : undefined}
            />
          )}
          <div
            className="card grow"
            style={{ overflow: "hidden", minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}
          >
            {table && (
              <div className="dt-tools">
                {allRows.length > 0 && <FilterRowButton open={filterRow} filtered={filtered} onToggle={setFilterRow} />}
                <Button
                  size="compact-sm"
                  variant={params.columns || view ? "light" : "default"}
                  leftSection={<Icon name="columns" size={15} />}
                  onClick={() => setDialog("columns")}
                >
                  {t("trialData.columns")}
                </Button>
                <Menu position="bottom-start">
                  <Menu.Target>
                    <Button size="compact-sm" variant="default" rightSection={<Icon name="chevronDown" size={12} />}>
                      {t("trialData.view")}: <b style={{ marginLeft: 4 }}>{view?.name ?? t("trialData.allColumns")}</b>
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={() => navigate({ search: (s) => ({ ...s, grid_view: undefined, columns: undefined }) })}>
                      {t("trialData.allColumns")}
                    </Menu.Item>
                    {views.views.map((v) => (
                      <Menu.Item
                        key={v.name}
                        onClick={() => navigate({ search: (s) => ({ ...s, grid_view: v.name, columns: undefined }) })}
                      >
                        {v.name}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
                {allRows.length > 0 && (
                  <FileButtons
                    viewOf={(kind) => grid.current?.view(kind) ?? null}
                    rows={allRows.length}
                    title={`${ev} ${th ?? ""} ${kis} ${table.label ?? params.table}`.replace(/\s+/g, " ")}
                    size="compact-sm"
                    excelHelp={t("trialData.exportHelp")}
                    printHelp={t("trialData.printHelp")}
                  />
                )}
                {canEdit && allRows.length > 0 && (
                  <Tooltip label={t("trialData.fill.buttonHelp")} multiline w={300}>
                    <Button size="compact-sm" variant="default" onClick={() => setDialog("fill")}>
                      {t("trialData.fill.button")}
                    </Button>
                  </Tooltip>
                )}
                <ResetTableButton
                  disabled={!layout.grid && !filter && !filterRow}
                  onReset={() => {
                    grid.current?.reset();
                    setFilter("");
                    setFilterRow(false);
                  }}
                />
                {shownView && <SortNote keys={shownView.sort} onClear={() => grid.current?.clearSort()} />}
                {shownView && filtered > 0 && (
                  <ShownNote shown={shownView.shown} total={shownView.total} onClear={() => grid.current?.clearFilters()}>
                    <Tooltip label={t("trialData.jumpToFirstMatchHelp")} multiline w={300}>
                      <Button size="compact-sm" variant="default" disabled={!shownView.shown} onClick={() => grid.current?.findFirst()}>
                        {t("trialData.jumpToFirstMatch")}
                      </Button>
                    </Tooltip>
                  </ShownNote>
                )}
              </div>
            )}
            {table && !rows.isPending && allRows.length === 0 ? (
              <p className="mu sm" style={{ padding: 16 }}>
                {t("trialData.emptyTrial")}
              </p>
            ) : table ? (
              <div style={{ flex: "1 1 0", minHeight: 0 }}>
                <EditableGrid
                  key={gridKey}
                  handle={grid}
                  table={table}
                  rows={allRows}
                  canEdit={canEdit}
                  save={save}
                  reload={reload}
                  visible={params.columns ?? view?.columns}
                  conflicts={conflicts}
                  edits={edits}
                  quickFilter={filter}
                  filterRow={filterRow}
                  onFocusCell={setCell}
                  onOpenHistory={(c) => {
                    setCell(c);
                    setPanel(true);
                  }}
                  onSelection={setSelection}
                  onConflicts={(found) => {
                    setConflicts(found);
                    setPanel(true);
                  }}
                  onSaved={(_, changes, { undo }) => {
                    markSaved();
                    // a Ctrl+Z puts the old Szf values back; what went to the line is undone in the Napló, not re-carried
                    if (!undo) carryKt(changes);
                  }}
                  onView={(v) => setGridView({ ...v, grid: gridKey })}
                  onUndoable={(count) => setUndoable({ grid: gridKey, count })}
                />
              </div>
            ) : null}
            {selected && (
              <SelectionBar
                actions={[
                  {
                    label: t("trialData.fill.selectedButton"),
                    onClick: () => setDialog("fill"),
                    hidden: !canEdit,
                  },
                  {
                    label: t("register.habit.action"),
                    onClick: () => setDialog("habit"),
                    hidden: !canEdit || !selectedLines.length,
                  },
                  {
                    label: t("trialData.deleteSelected", { count: selected.rowIds.length }),
                    icon: "trash",
                    danger: true,
                    onClick: () => setDialog("delete"),
                    hidden: !canDelete,
                  },
                ]}
                count={selected.cells}
                label={t("trialData.selectionCount", { count: selected.cells, rows: selected.rowIds.length })}
                stats={
                  selected.numbers
                    ? t("trialData.selectionStats", {
                        sum: formatNumber(selected.sum),
                        mean: formatNumber(selected.mean),
                        min: formatNumber(selected.min),
                        max: formatNumber(selected.max),
                      })
                    : undefined
                }
                onClear={() => grid.current?.clearSelection()}
              />
            )}
          </div>
          <div className="hstack" style={{ gap: 14, fontSize: "var(--b-text-small, 0.75rem)", color: "var(--b-ink-3)", flexWrap: "wrap" }}>
            <span>{t("trialData.rowCount", { count: formatNumber(allRows.length) })}</span>
            {rows.hasNextPage && (
              <Button size="compact-xs" variant="light" onClick={() => void rows.fetchNextPage()} loading={rows.isFetchingNextPage}>
                {t("trialData.loadMore")}
              </Button>
            )}
            <div style={{ flexGrow: 1 }} />
            {GRID_SHORTCUTS.slice(0, 2).map(([label, key]) => (
              <span key={label}>
                <Kbd>{key}</Kbd> {t(`shortcuts.${label}`)}
              </span>
            ))}
            <AllShortcuts />
          </div>
        </div>
        {panel && table && (
          <>
            <Splitter cssVar="--cell-panel-w" side="end" min={240} max={640} label={t("trialData.history.panelWidth")} />
            <CellPanel
              table={table}
              cell={cell}
              conflicts={conflicts}
              canEdit={canEdit}
              save={(change) =>
                save([change])
                  .then(() => {
                    markSaved();
                    reload();
                    carryKt([change]);
                  })
                  .catch(notifyError)
              }
              dismissConflict={(c) => setConflicts((all) => all.filter((x) => x !== c))}
              onClose={() => setPanel(false)}
            />
          </>
        )}
      </div>

      {kt && (
        <LineKtModal
          plan={kt.plan}
          running={ktRunning}
          onOverwrite={() => ktAnswer(true)}
          onBlanks={() => ktAnswer(false)}
          onClose={ktNext}
        />
      )}
      {dialog === "columns" && table && (
        <ColumnsDialog
          table={table}
          view={params.columns && view ? { ...view, columns: params.columns } : view}
          onSave={(name, columns) => {
            views.save({ name, columns });
            void navigate({ search: (s) => ({ ...s, grid_view: name, columns: undefined }) });
            setDialog(null);
          }}
          onDelete={(name) => {
            views.remove(name);
            void navigate({ search: (s) => ({ ...s, grid_view: undefined, columns: undefined }) });
            setDialog(null);
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "rows" && table && ev && (
        <CreateRowsDialog
          trials={trials}
          defaultSite={th ?? defaultTh}
          trial={kis}
          from={params.parc_from}
          to={params.parc_to}
          onClose={() => setDialog(null)}
          onCreate={({ site, trial: target, subplots, from, to }) =>
            measurementsCreateRows(ev, table.name, { th: site, kis: target, subplots, parc_from: from, parc_to: to })
              .then((r) => {
                done(r.count ? t("trialData.rowsCreated", { count: r.count }) : t("trialData.noNewRows"));
                void overview.refetch();
                // the grid then shows the rows made: the site, the trial or group and the plot range
                void navigate({ search: (s) => ({ ...s, th: site, kis: target, parc_from: from ?? undefined, parc_to: to ?? undefined }) });
              })
              .catch(notifyError)
          }
        />
      )}
      {dialog === "average" && table && th && ev && (
        <ConfirmModal
          title={t("trialData.subplotAverage")}
          text={t("trialData.subplotAverageConfirm")}
          confirm={t("trialData.apply")}
          onClose={() => setDialog(null)}
          onConfirm={() =>
            measurementsSubplotAverage(ev, table.name, { th, kis, columns: params.columns ?? view?.columns ?? null })
              .then((r) => done(t("trialData.subplotAverageDone", { count: r.count })))
              .catch(notifyError)
          }
        />
      )}
      {dialog === "fill" && (
        <FillDialog
          grid={grid}
          onClose={() => setDialog(null)}
          onFilled={(count) => {
            setDialog(null);
            notifications.show({ color: "green", message: t("trialData.fill.done", { count }) });
          }}
        />
      )}
      {dialog === "habit" && (
        <HabitModal
          lines={selectedLines}
          onClose={() => setDialog(null)}
          onDone={() => {
            reload();
            void client.invalidateQueries({ queryKey: ["register-habit"] });
          }}
        />
      )}
      {dialog === "delete" && table && ev && selection && (
        <ConfirmModal
          title={t("trialData.deleteRows")}
          text={t("trialData.deleteRowsConfirm", { count: selection.rowIds.length })}
          confirm={t("trialData.deleteRows")}
          danger
          onClose={() => setDialog(null)}
          onConfirm={() =>
            measurementsDeleteRows(ev, table.name, { row_ids: selection.rowIds })
              .then((r) => done(t("trialData.rowsDeleted", { count: r.count })))
              .catch(notifyError)
          }
        />
      )}
    </div>
  );
}

type CreateRows = { site: string; trial: string; subplots: boolean; from: number | null; to: number | null };

/** Legacy Manual Input's empty structure: the site, the trial or a trial group (MV24*), a plot range (each end may
 *  stay open) and the subplots. */
function CreateRowsDialog({
  trials,
  defaultSite,
  trial: defaultTrial,
  from: defaultFrom,
  to: defaultTo,
  onCreate,
  onClose,
}: {
  trials: TrialOverviewOut[];
  defaultSite: string;
  trial: string;
  from: number | undefined;
  to: number | undefined;
  onCreate: (rows: CreateRows) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [site, setSite] = useState(defaultSite);
  const [trial, setTrial] = useState(defaultTrial);
  const [from, setFrom] = useState<number | string>(defaultFrom ?? "");
  const [to, setTo] = useState<number | string>(defaultTo ?? "");
  const [subplots, setSubplots] = useState(false);
  // the site choices and the count follow the trial or group typed; an exact trial goes in its own spelling
  const typed = trial.trim();
  const target = resolveTrial(typed, trials);
  const sites = target?.sites ?? [];
  const broad = trialGroupTooBroad(typed);
  const matchHint = !typed
    ? undefined
    : broad
      ? t("trialData.groupTooBroad")
      : !target
        ? t("trialData.noSuchTrial")
        : isTrialGroup(typed)
          ? t("trialData.groupMatches", { count: target.members, plots: formatNumber(target.register_plots) })
          : undefined;
  return (
    <Modal opened onClose={onClose} title={<b>{t("trialData.prepare")}</b>}>
      <Stack>
        <Text size="sm">{t("trialData.prepareDescription")}</Text>
        {sites.length ? (
          <Select
            placeholder={hint("searchSelect")}
            label={t("trialData.site")}
            data={[...new Set([...sites, defaultSite])]}
            value={site}
            onChange={(v) => v && setSite(v)}
            searchable
            allowDeselect={false}
          />
        ) : (
          <TextInput label={t("trialData.site")} value={site} onChange={(e) => setSite(e.currentTarget.value.toUpperCase())} />
        )}
        <TextInput
          label={t("trialData.trialOrGroup")}
          description={t("trialData.groupHelp")}
          value={trial}
          onChange={(e) => setTrial(e.currentTarget.value)}
          error={broad ? matchHint : undefined}
        />
        {matchHint && !broad && (
          <Text size="sm" c="dimmed">
            {matchHint}
          </Text>
        )}
        <Group gap="sm">
          <PlotRange
            from={from}
            to={to}
            onFrom={setFrom}
            onTo={setTo}
            fromLabel={t("trialData.plotFrom")}
            toLabel={t("trialData.plotTo")}
          />
        </Group>
        <Checkbox label={t("trialData.withSubplots")} checked={subplots} onChange={(e) => setSubplots(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("shell.cancel")}
          </Button>
          <Button
            disabled={!site.trim() || !typed || broad}
            onClick={() =>
              onCreate({
                site: site.trim(),
                trial: target && !isTrialGroup(typed) ? target.kis : typed,
                subplots,
                from: num(from),
                to: num(to),
              })
            }
          >
            {t("trialData.apply")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** The most frequent of the tab counts: a tab shows its count only when it differs (every table of a 24-plot trial
 *  with three subplots has 72 rows; the number says something only for the odd one). */
function mostCommon(values: number[]): number | undefined {
  const seen = new Map<number, number>();
  for (const v of values) seen.set(v, (seen.get(v) ?? 0) + 1);
  return [...seen.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
}

/** The foot's "? Minden billentyűparancs": the shell's shortcut list (its own component, so the page does not
 *  follow the shell's state). */
function AllShortcuts() {
  const { t } = useTranslation();
  const { openShortcuts } = useShell();
  return (
    <Button size="compact-xs" variant="subtle" onClick={openShortcuts}>
      <Kbd mr={6}>?</Kbd> {t("trialData.allShortcuts")}
    </Button>
  );
}
